import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DarkModeToggle from './DarkModeToggle';
import { SUBSCRIPTION_TIERS, SubscriptionTier } from '../config/subscriptions';

const UpgradePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpgrade = async (tier: SubscriptionTier) => {
    if (!tier.stripePriceId) {
      setError(`Invalid subscription plan selected. Price ID: ${tier.stripePriceId}`);
      return;
    }

    if (user?.isSubscribed) {
      setError('You are already subscribed. Contact support to change your plan.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestPayload = {
        priceId: tier.stripePriceId,
        successUrl: `${window.location.origin}/profile?upgrade=success&tier=${tier.id}`,
        cancelUrl: `${window.location.origin}/upgrade`,
        metadata: {
          tier: tier.id,
          user_id: user?.id || 'anonymous',
          upgrade_flow: 'true'
        },
        customerEmail: user?.email || undefined
      };

      console.log('Creating upgrade checkout session:', requestPayload);

      // Use the same Cloudflare Workers endpoint as signup
      const endpoint = 'https://eightythreelabs-subscription-checkout.yahya-darman-work.workers.dev/';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      
      if (url) {
        console.log('Redirecting to Stripe checkout:', url);
        // Redirect to Stripe checkout
        window.location.href = url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Upgrade checkout error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to create checkout: ${errorMessage}`);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/" 
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              ← Back to Dashboard
            </Link>
            <div className="flex items-center space-x-4">
              <DarkModeToggle />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            🚀 EightyThreeLabs is Now Premium-Only
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            We've upgraded our platform with advanced features and personalized learning experiences. 
            Choose your plan to unlock the full potential of mathematical excellence.
          </p>
          
          {user?.isSubscribed ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 max-w-md mx-auto">
              <div className="text-green-600 dark:text-green-400 text-2xl mb-2">✅</div>
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-1">
                You're Already Premium!
              </h3>
              <p className="text-green-700 dark:text-green-400 text-sm">
                Enjoy all the advanced features and keep excelling at mathematics.
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 max-w-md mx-auto">
              <div className="text-blue-600 dark:text-blue-400 text-2xl mb-2">💎</div>
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-1">
                Ready to Upgrade?
              </h3>
              <p className="text-blue-700 dark:text-blue-400 text-sm">
                Choose your plan below and unlock advanced mathematical learning.
              </p>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 max-w-md mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start">
                <span className="text-red-500 text-lg mr-3">⚠️</span>
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Plan Selection */}
        <div className="max-w-lg mx-auto mb-12">
          {SUBSCRIPTION_TIERS.map((tier) => (
            <div
              key={tier.id}
              className="relative bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 border-blue-500 dark:border-blue-400"
            >
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-xs font-bold">
                  LIMITED TIME OFFER
                </span>
              </div>
              
              <div className="p-8">
                <div className="text-center mb-6">
                  <div className="text-4xl mb-2">🎉</div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {tier.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">
                    {tier.description}
                  </p>
                </div>

                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-gray-900 dark:text-gray-100">
                    {tier.priceDisplay}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm">
                    per {tier.interval}
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                      <span className="text-gray-700 dark:text-gray-300 text-sm">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgrade(tier)}
                  disabled={user?.isSubscribed || loading}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                    loading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : user?.isSubscribed 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white hover:from-yellow-600 hover:to-orange-700'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating checkout...
                    </div>
                  ) : user?.isSubscribed ? (
                    'Already Subscribed'
                  ) : (
                    `Upgrade to ${tier.name}`
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Why Premium Only */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
            🔥 Why We Went Premium-Only
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Focused Experience
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                No more limitations or feature restrictions. Every user gets the complete mathematical learning experience.
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl mb-3">⚡</div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Premium Features
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Advanced AI coaching, detailed analytics, and personalized learning paths are now standard for everyone.
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl mb-3">🏆</div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Elite Community
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Join a community of serious mathematics learners committed to excellence and continuous improvement.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                What happened to the free plan?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                We've evolved into a premium platform to provide the best possible learning experience. All advanced features that were previously premium are now included in every subscription.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Can I cancel anytime?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Is there a money-back guarantee?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Absolutely! We offer a 30-day money-back guarantee if you're not completely satisfied with your experience.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;